/**
 * External Storage Integration
 *
 * This module handles video storage in Google Drive or Dropbox
 * to save on Supabase storage costs. Videos are stored externally,
 * while metadata (URLs, names) are stored in Supabase.
 *
 * Setup Options:
 * 1. Google Drive: Use Google Picker API for uploads
 * 2. Dropbox: Use Dropbox Chooser/Saver
 * 3. Manual: User uploads to their own storage and pastes URL
 */

// ============================================
import { getConfig } from 'src/api/runtime-config';

// Storage Provider Types
// ============================================

export type StorageProvider = 'google_drive' | 'dropbox' | 'supabase' | 'manual';

export interface ExternalFile {
  url: string;
  name: string;
  mimeType?: string;
  size?: number;
  provider: StorageProvider;
  externalId?: string; // Google Drive file ID or Dropbox path
}

// ============================================
// Google Drive Integration
// ============================================

interface GooglePickerConfig {
  clientId: string;
  apiKey: string;
  appId: string;
}

// Check if Google Picker is available
export const isGooglePickerAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).google?.picker;
};

/**
 * Load the Google Picker API
 * Add this script to your HTML: https://apis.google.com/js/api.js
 */
export const loadGooglePickerApi = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGooglePickerAvailable()) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      (window as any).gapi.load('picker', { callback: resolve });
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Open Google Drive picker to select a video
 */
export const openGoogleDrivePicker = async (
  config: GooglePickerConfig,
  accessToken: string
): Promise<ExternalFile | null> => {
  await loadGooglePickerApi();

  return new Promise((resolve) => {
    const google = (window as any).google;

    const picker = new google.picker.PickerBuilder()
      .addView(
        new google.picker.DocsView()
          .setMimeTypes('video/mp4,video/webm,video/quicktime,video/x-msvideo')
          .setMode(google.picker.DocsViewMode.LIST)
      )
      .setOAuthToken(accessToken)
      .setDeveloperKey(config.apiKey)
      .setAppId(config.appId)
      .setCallback((data: any) => {
        if (data.action === google.picker.Action.PICKED) {
          const file = data.docs[0];
          resolve({
            url: `https://drive.google.com/file/d/${file.id}/view`,
            name: file.name,
            mimeType: file.mimeType,
            size: file.sizeBytes,
            provider: 'google_drive',
            externalId: file.id,
          });
        } else if (data.action === google.picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();

    picker.setVisible(true);
  });
};

/**
 * Upload a file to Google Drive
 * Requires OAuth token with drive.file scope
 */
export const uploadToGoogleDrive = async (
  file: File,
  accessToken: string,
  folderId?: string
): Promise<ExternalFile> => {
  const metadata = {
    name: file.name,
    mimeType: file.type,
    ...(folderId && { parents: [folderId] }),
  };

  const form = new FormData();
  form.append(
    'metadata',
    new Blob([JSON.stringify(metadata)], { type: 'application/json' })
  );
  form.append('file', file);

  const response = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,size,webViewLink',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: form,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to upload to Google Drive');
  }

  const result = await response.json();

  // Make the file viewable by anyone with the link
  await fetch(
    `https://www.googleapis.com/drive/v3/files/${result.id}/permissions`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        role: 'reader',
        type: 'anyone',
      }),
    }
  );

  return {
    url: result.webViewLink || `https://drive.google.com/file/d/${result.id}/view`,
    name: result.name,
    mimeType: result.mimeType,
    size: parseInt(result.size),
    provider: 'google_drive',
    externalId: result.id,
  };
};

/**
 * Get a direct embed/stream URL for a Google Drive video
 */
export const getGoogleDriveEmbedUrl = (fileId: string): string => {
  return `https://drive.google.com/file/d/${fileId}/preview`;
};

/**
 * Get a direct download URL for a Google Drive file
 */
export const getGoogleDriveDownloadUrl = (fileId: string): string => {
  return `https://drive.google.com/uc?export=download&id=${fileId}`;
};

// ============================================
// Dropbox Integration
// ============================================

interface DropboxConfig {
  appKey: string;
}

// Check if Dropbox Chooser is available
export const isDropboxAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).Dropbox;
};

/**
 * Load the Dropbox SDK
 * Add this script to your HTML: https://www.dropbox.com/static/api/2/dropins.js
 */
export const loadDropboxSdk = (appKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isDropboxAvailable()) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
    script.id = 'dropboxjs';
    script.dataset.appKey = appKey;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Open Dropbox Chooser to select a video
 */
export const openDropboxChooser = async (
  config: DropboxConfig
): Promise<ExternalFile | null> => {
  await loadDropboxSdk(config.appKey);

  return new Promise((resolve) => {
    const Dropbox = (window as any).Dropbox;

    Dropbox.choose({
      success: (files: any[]) => {
        const file = files[0];
        resolve({
          url: file.link,
          name: file.name,
          size: file.bytes,
          provider: 'dropbox',
          externalId: file.id,
        });
      },
      cancel: () => resolve(null),
      linkType: 'direct', // Get a direct link that can be used in video players
      multiselect: false,
      extensions: ['.mp4', '.webm', '.mov', '.avi'],
    });
  });
};

/**
 * Open Dropbox Saver to upload a file
 */
export const saveToDropbox = async (
  url: string,
  filename: string,
  config: DropboxConfig
): Promise<void> => {
  await loadDropboxSdk(config.appKey);

  return new Promise((resolve, reject) => {
    const Dropbox = (window as any).Dropbox;

    Dropbox.save(url, filename, {
      success: () => resolve(),
      cancel: () => reject(new Error('Upload cancelled')),
      error: (errorMessage: string) => reject(new Error(errorMessage)),
    });
  });
};

// ============================================
// Manual URL Entry (Simplest Option)
// ============================================

/**
 * Validate a video URL
 */
export const validateVideoUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);

    // Check for common video hosting services
    const validHosts = [
      'drive.google.com',
      'www.dropbox.com',
      'dl.dropboxusercontent.com',
      'vimeo.com',
      'player.vimeo.com',
      'youtube.com',
      'www.youtube.com',
      'youtu.be',
      'loom.com',
      'www.loom.com',
    ];

    // Allow any HTTPS URL (user's own hosting)
    if (parsed.protocol === 'https:') {
      return true;
    }

    return validHosts.some((host) => parsed.hostname.includes(host));
  } catch {
    return false;
  }
};

/**
 * Detect the provider from a URL
 */
export const detectProviderFromUrl = (url: string): StorageProvider => {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (hostname.includes('drive.google.com')) return 'google_drive';
    if (hostname.includes('dropbox.com') || hostname.includes('dropboxusercontent.com')) return 'dropbox';

    return 'manual';
  } catch {
    return 'manual';
  }
};

/**
 * Extract Google Drive file ID from URL
 */
export const extractGoogleDriveId = (url: string): string | null => {
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /id=([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
};

/**
 * Create an ExternalFile object from a manually entered URL
 */
export const createExternalFileFromUrl = (url: string, name?: string): ExternalFile => {
  const provider = detectProviderFromUrl(url);
  let externalId: string | undefined;

  if (provider === 'google_drive') {
    externalId = extractGoogleDriveId(url) || undefined;
  }

  return {
    url,
    name: name || url.split('/').pop() || 'video',
    provider,
    externalId,
  };
};

// ============================================
// Storage Manager (Unified Interface)
// ============================================

export interface StorageManagerConfig {
  preferredProvider: StorageProvider;
  googleDrive?: GooglePickerConfig & { accessToken?: string };
  dropbox?: DropboxConfig;
}

export class ExternalStorageManager {
  private config: StorageManagerConfig;

  constructor(config: StorageManagerConfig) {
    this.config = config;
  }

  /**
   * Upload a video file to the preferred provider
   */
  async uploadVideo(file: File): Promise<ExternalFile> {
    const { preferredProvider } = this.config;

    switch (preferredProvider) {
      case 'google_drive':
        if (!this.config.googleDrive?.accessToken) {
          throw new Error('Google Drive access token required');
        }
        return uploadToGoogleDrive(file, this.config.googleDrive.accessToken);

      case 'dropbox':
        // Dropbox doesn't support direct upload via Chooser
        // User needs to upload manually and paste link
        throw new Error('Direct Dropbox upload not supported. Please upload manually and paste the link.');

      case 'manual':
      default:
        throw new Error('Please upload your video to your preferred storage and paste the link.');
    }
  }

  /**
   * Open a picker to select an existing video
   */
  async selectVideo(): Promise<ExternalFile | null> {
    const { preferredProvider } = this.config;

    switch (preferredProvider) {
      case 'google_drive':
        if (!this.config.googleDrive) {
          throw new Error('Google Drive configuration required');
        }
        return openGoogleDrivePicker(
          this.config.googleDrive,
          this.config.googleDrive.accessToken || ''
        );

      case 'dropbox':
        if (!this.config.dropbox) {
          throw new Error('Dropbox configuration required');
        }
        return openDropboxChooser(this.config.dropbox);

      default:
        return null;
    }
  }

  /**
   * Get an embeddable URL for a video
   */
  getEmbedUrl(file: ExternalFile): string {
    if (file.provider === 'google_drive' && file.externalId) {
      return getGoogleDriveEmbedUrl(file.externalId);
    }

    // For Dropbox and manual, use the direct URL
    return file.url;
  }
}

// ============================================
// React Hook for External Storage
// ============================================

// This can be used in React components
export const createStorageConfig = (): StorageManagerConfig => {
  const driveClientId = getConfig('REACT_APP_GOOGLE_DRIVE_CLIENT_ID');
  const dropboxKey = getConfig('REACT_APP_DROPBOX_APP_KEY');
  return {
    preferredProvider: 'manual', // Default to manual URL entry (simplest)
    googleDrive: driveClientId
      ? {
          clientId: driveClientId,
          apiKey: getConfig('REACT_APP_GOOGLE_DRIVE_API_KEY'),
          appId: getConfig('REACT_APP_GOOGLE_DRIVE_APP_ID'),
        }
      : undefined,
    dropbox: dropboxKey
      ? {
          appKey: dropboxKey,
        }
      : undefined,
  };
};
